import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { EVENT_ADJUSTMENT_CATEGORIES } from '@app/events/shared/event-adjustment.model';
import { EventAdjustmentPayload } from '@app/events/shared/event-adjustment.service';

/**
 * TVG-156: admin-only form to add a discount / adjustment line to an event
 * finance. A signed figure (positive adds, negative subtracts), a free-text
 * description and a coding category. Emits the payload to the parent which
 * persists it and refreshes the financial summary.
 */
@Component({
  selector: 'app-adjustment-line-form',
  standalone: true,
  imports: [TextInputComponent, SelectComponent, ButtonComponent],
  template: `
    <div class="flex flex-col gap-5 p-1">
      <div>
        <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Add discount / adjustment</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Admin only. Use a negative figure for a discount or credit, or a positive figure for a
          top up such as a minimum spend deficit. The line is saved against this event only and never
          appears in the product catalogue.
        </p>
      </div>

      <app-text-input
        [control]="form.controls.description"
        [label]="'Description'"
        [name]="'description'"
        [placeholder]="'e.g. Goodwill discount against food'"
        [required]="true"
      ></app-text-input>

      <app-text-input
        [control]="form.controls.amount"
        [label]="'Amount'"
        [name]="'amount'"
        [type]="'number'"
        [prefix]="'£'"
        [allowNegativeNumbers]="true"
        [required]="true"
        [helpText]="'Negative subtracts from the running balance, positive adds to it.'"
      ></app-text-input>

      <app-select
        [control]="form.controls.category"
        [label]="'Category'"
        [choices]="categoryChoices"
        [placeholder]="'Select a category'"
        [required]="true"
      ></app-select>

      @if (serverError) {
        <div class="text-sm text-red-600 dark:text-red-400">{{ serverError }}</div>
      }

      <div class="flex justify-end gap-3 pt-2">
        <app-button [label]="'Cancel'" [style]="'secondary'" (click)="cancelled.emit()"></app-button>
        <app-button
          [label]="submitLabel"
          [style]="'primary'"
          [disabled]="form.invalid || processing"
          (click)="save()"
        ></app-button>
      </div>
    </div>
  `,
})
export class AdjustmentLineFormComponent {
  @Input() submitLabel = 'Add line';
  @Input() processing = false;

  @Output() created = new EventEmitter<EventAdjustmentPayload>();
  @Output() cancelled = new EventEmitter<void>();

  protected serverError: string | null = null;

  protected readonly categoryChoices: { value: string; label: string }[] =
    EVENT_ADJUSTMENT_CATEGORIES.map((category) => ({ value: category, label: category }));

  protected form = new FormGroup({
    description: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
    amount: new FormControl<number | null>(null, { validators: [Validators.required] }),
    category: new FormControl<string>('Discount', { nonNullable: true, validators: [Validators.required] }),
  });

  save(): void {
    this.serverError = null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.created.emit({
      description: value.description,
      amount: Number(value.amount),
      category: value.category,
    });
  }

  applyServerError(message: string): void {
    this.serverError = message;
  }

  reset(): void {
    this.form.reset({ description: '', amount: null, category: 'Discount' });
    this.serverError = null;
  }
}
